/**
 * JourneyAuthorSection — the team-side "Client Hub" branch in the outline.
 *
 * A top-level project branch (peer to Schedule and Reference). Renders the
 * project's journey entries as outline nodes grouped by phase and sorted by
 * date within each phase (the same order the client sees — no manual
 * reordering; the date is the order). Each entry expands to show what it links
 * and the client's response state. Authoring (add / edit / publish / remove /
 * copy link / activate) lives in the node ⋯ menus. The entry editor itself is
 * JourneyEntryModal (opened via event).
 *
 * Outline-first: rows → triangle / bullet / text / menu, expandable children.
 * Lazy-loads the journey (getOrCreateLink → listEntries + listResponses) when the
 * branch is first expanded. Reloads on the 'journey-updated' event.
 */

import { useEffect, useReducer, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { wf, saveViewState } from '../outline/state.js'
import { ctx } from '../outline/ctx.js'
import { ActionMenu, ActionMenuButton, ActionMenuItem, ActionMenuDivider, useActionMenu } from './ActionMenu.jsx'
import { getModule } from '../journey/modules/index.js'
import { PROPOSAL_BUCKET } from '../journey/journeyOrder.js'
import {
  getOrCreateLink, listEntries, listResponses, listJourneyUsers,
  deleteEntry, upsertEntry, setStatus,
} from '../services/journey.js'
import { getProjectPhases } from '../services/phases.js'
import { fmtJourneyDate } from '../journey/journeyUI.jsx'
import { confirmDialog, promptDialog } from './Dialog.jsx'

const getFirst = (v) => Array.isArray(v) ? v[0] : v

// Resolved phase for a raw journey entry (author side — entries aren't enriched with milestone).
// Milestone-backed → the milestone's Project Phase (from loaded project state); else a manual
// data.phase; else the onboarding default; else 'Unassigned'.
function resolveEntryPhase(entry) {
  if (entry.milestone_airtable_id) {
    const ms = (ctx.state?.milestones || []).find(m => m.id === entry.milestone_airtable_id)
    // The milestone's Phase LINK first (same as the Client Hub), its Project Phase text as fallback.
    const phId = ms && getFirst(ms.fields?.['Phase'])
    const ph = phId && (ctx.state?.phases || []).find(x => x.id === phId)
    const p = getFirst(ph?.fields?.['Phase Name']) || (ms && getFirst(ms.fields?.['Project Phase']))
    if (p) return p
  }
  return (entry.data && entry.data.phase)
    || (entry.type === 'onboarding-questionnaire' ? 'Pre-design & Planning Phase' : '')
    || (entry.type === 'proposal' ? PROPOSAL_BUCKET : '')
    || 'Unassigned'
}

// Resolved ISO date for a raw journey entry (mirrors the Client Hub): the milestone's post
// date when milestone-backed, else the manually-set data.entryDate. Empty string if neither.
function resolveEntryDate(entry) {
  if (entry.data && entry.data.hubDate) return entry.data.hubDate   // a milestone card's date set by hand
  if (entry.milestone_airtable_id) {
    const ms = (ctx.state?.milestones || []).find(m => m.id === entry.milestone_airtable_id)
    const d = ms && getFirst(ms.fields?.['Posted Date'])
    if (d) return d
  }
  return (entry.data && entry.data.entryDate) || ''
}

// The entry's actual title for the outline row: an explicit title, else the linked milestone's
// real name, else the module label. (Not the bare category — that stays as the small type badge.)
function resolveEntryTitle(entry, mod) {
  if (entry.title) return entry.title
  if (entry.milestone_airtable_id) {
    const ms = (ctx.state?.milestones || []).find(m => m.id === entry.milestone_airtable_id)
    const name = ms && (getFirst(ms.fields?.['Full Deliverable Name']) || getFirst(ms.fields?.['Name (from Deliverable)']))
    return name || entry.milestone_type || (mod && mod.label) || entry.type
  }
  return (mod && mod.label) || entry.type
}

// Branches = every project phase (so entries can be added under any), in Sort Order, plus any phase
// that appears on an entry but isn't in the project list (e.g. 'Unassigned'), appended after.
// Entries within each phase sort by date ascending — the SAME ordering the Client Hub uses
// (orderEntries in journey/useJourney.js: date asc, undated last, stored position as the stable
// tiebreak) — so the author view always reads in the order the client will see. There is no
// manual reordering; the date IS the order.
function buildAuthorGroups(entries, phaseList) {
  const byPhase = {}
  entries.forEach((e, i) => { const ph = resolveEntryPhase(e); (byPhase[ph] = byPhase[ph] || []).push({ e, i }) })
  Object.values(byPhase).forEach(items => items.sort((a, b) => {
    const da = resolveEntryDate(a.e), db = resolveEntryDate(b.e)
    if (da && db && da !== db) return da < db ? -1 : 1
    if (da && !db) return -1
    if (!da && db) return 1
    return a.i - b.i
  }))
  const order = []
  // Proposals predate every real phase — put that bucket first if present, ahead of the actual
  // phase list, instead of letting it fall into the generic append-after-known-phases behavior
  // below (which is right for a true 'Unassigned' catch-all, but wrong for a proposal).
  if (byPhase[PROPOSAL_BUCKET]) order.push(PROPOSAL_BUCKET)
  ;(phaseList || []).forEach(p => { if (!order.includes(p)) order.push(p) })
  Object.keys(byPhase).forEach(p => { if (!order.includes(p)) order.push(p) })
  return order.map(ph => ({ phase: ph, items: byPhase[ph] || [] }))
}

/** Normalize a possibly-array / JSON-stringified-array lookup value to a plain string. */
function cleanName(v) {
  if (Array.isArray(v)) return v[0] || ''
  if (typeof v === 'string') {
    const s = v.trim()
    if (s.startsWith('[')) { try { const a = JSON.parse(s); return Array.isArray(a) ? (a[0] || '') : s } catch { /* fall through */ } }
    return s
  }
  return ''
}

function calcVis() {
  if (!wf.zoomProjectId) return 'hidden'
  if (!wf.zoomRoot && !wf.zoomPhaseId && !wf.zoomMsId && !wf.zoomSection &&
      !wf.zoomCat && !wf.zoomItemId && !wf.zoomConceptId && !wf.searchQuery) return 'home'
  return 'hidden'
}

export default function JourneyAuthorSection() {
  const [, forceUpdate] = useReducer(v => v + 1, 0)
  const [journey, setJourney] = useState(null)
  const [entries, setEntries] = useState(null)        // null = not loaded
  const [responses, setResponses] = useState([])
  const [phaseList, setPhaseList] = useState([])      // project phase names, Sort Order
  const loadedRef = useRef(false)

  useEffect(() => {
    function onRender() { forceUpdate() }
    window.addEventListener('outline-render', onRender)
    return () => window.removeEventListener('outline-render', onRender)
  }, [])

  const projId = wf.zoomProjectId || ctx.recordId || ''

  async function load() {
    // Prefer the project's own record (reliable even pre-conversion, before any milestones
    // exist) over a milestone lookup field, which is empty for opportunities with zero
    // milestones — that emptiness used to silently block getOrCreateLink's self-heal below.
    const projRec = (ctx.projectsCache || []).find(p => p.id === projId)
    const projName = cleanName(projRec?.fields?.['Project Name'])
      || cleanName((ctx.state?.milestones || [])[0]?.fields?.['Project Name'])
    try {
      const { journey: j } = await getOrCreateLink(projId, projName)
      setJourney(j)
      const [{ entries: es }, { responses: rs }] = await Promise.all([
        listEntries(j.id),
        listResponses(j.id),
      ])
      setEntries(es || [])
      setResponses(rs || [])
      getProjectPhases(projId)
        .then(recs => {
          const seen = new Set(), out = []
          ;(recs || []).forEach(r => { const n = getFirst(r.fields?.['Phase Name']); if (n && !seen.has(n)) { seen.add(n); out.push(n) } })
          setPhaseList(out)
        })
        .catch(() => setPhaseList([]))
    } catch (e) {
      console.error('[JourneyAuthorSection] load failed', e)
      setEntries([])
    }
  }

  function addEntryToPhase(phase) {
    if (!journey) return
    window.dispatchEvent(new CustomEvent('open-journey-entry-modal', {
      detail: { journeyId: journey.id, presetPhase: phase && phase !== 'Unassigned' ? phase : undefined },
    }))
  }

  // Expand/collapse every phase branch (and entry) under the journey — persisted via wf.collapsed.
  function phaseKey(phase) { return 'jphase:' + (journey?.id || projId) + ':' + phase }
  function setAllCollapsed(collapsed) {
    buildAuthorGroups(entries || [], phaseList).forEach(g => { wf.collapsed[phaseKey(g.phase)] = collapsed })
    ;(entries || []).forEach(e => { wf.collapsed['jentry:' + e.id] = collapsed })
    if (!collapsed) wf.collapsed['root:journey'] = false
    saveViewState()
    window.dispatchEvent(new Event('outline-render'))
  }

  function reload() { if (journey) { listEntries(journey.id).then(({ entries: es }) => setEntries(es || [])); listResponses(journey.id).then(({ responses: rs }) => setResponses(rs || [])) } }

  // Reload when an entry is saved elsewhere (editor modal / menu shortcuts)
  useEffect(() => {
    function onUpdated() { if (loadedRef.current) reload() }
    window.addEventListener('journey-updated', onUpdated)
    return () => window.removeEventListener('journey-updated', onUpdated)
  }) // eslint-disable-line react-hooks/exhaustive-deps

  const collapsed = wf.collapsed['root:journey'] !== false

  // Lazy-load on first expand / project change
  useEffect(() => {
    if (!collapsed && !loadedRef.current && projId) { loadedRef.current = true; load() }
  }) // eslint-disable-line react-hooks/exhaustive-deps

  // Reset when the project changes
  const projRef = useRef(projId)
  if (projRef.current !== projId) { projRef.current = projId; loadedRef.current = false; if (journey) { setJourney(null); setEntries(null) } }

  if (calcVis() === 'hidden') return null

  function addEntry() {
    if (!journey) return
    window.dispatchEvent(new CustomEvent('open-journey-entry-modal', { detail: { journeyId: journey.id } }))
  }
  function previewDrafts() {
    if (!journey) return
    window.dispatchEvent(new CustomEvent('open-journey-preview', { detail: { journeyId: journey.id } }))
  }
  function toggleActive() {
    if (!journey) return
    const next = journey.status === 'active' ? 'disabled' : 'active'
    setStatus(journey.id, next).then(({ journey: j }) => setJourney(j || { ...journey, status: next }))
  }
  // 2026-07: access is per-recipient now (database/134_journey_recipients.sql) — a husband and
  // wife each get their own link+PIN, individually revocable. There's no single "the client link"
  // to copy or regenerate anymore; all of that (add recipients from the project's Client team +
  // manual emails, resend/regenerate/revoke each individually, plus retiring any old shared
  // credential a legacy link is still using) lives in one modal.
  function openManageAccess() {
    if (!journey) return
    window.dispatchEvent(new CustomEvent('open-send-journey-access-modal', { detail: { journeyId: journey.id, projectId: projId } }))
  }

  return (
    <div className="wf-list">
      <JourneyRoot
        collapsed={collapsed}
        journey={journey}
        onAdd={addEntry}
        onExpandAll={() => setAllCollapsed(false)}
        onCollapseAll={() => setAllCollapsed(true)}
        onPreviewDrafts={previewDrafts}
        onManageAccess={openManageAccess}
        onToggleActive={toggleActive}
      />
      {!collapsed && (
        <div className="wf-children">
          {entries === null && <div className="wf-node-text" style={{ padding: '6px 0', color: '#7a9aaa' }}>Loading…</div>}
          {entries && buildAuthorGroups(entries, phaseList).map(({ phase, items }) => (
            <PhaseBranch
              key={phase}
              phase={phase}
              count={items.length}
              ckey={phaseKey(phase)}
              onAdd={() => addEntryToPhase(phase)}
            >
              {items.length === 0 ? (
                <div className="wf-node-text" style={{ padding: '4px 0', color: '#7a9aaa', fontStyle: 'italic' }}>
                  No entries in this phase — use ⋯ Add entry here.
                </div>
              ) : items.map(({ e }) => (
                <EntryNode
                  key={e.id}
                  entry={e}
                  journey={journey}
                  projectId={projId}
                  responded={responses.some(r => r.entry_id === e.id)}
                  phaseList={phaseList}
                  onChanged={reload}
                />
              ))}
            </PhaseBranch>
          ))}
          {journey && <UsersBranch journeyId={journey.id} />}
        </div>
      )}
    </div>
  )
}

function JourneyRoot({ collapsed, journey, onAdd, onExpandAll, onCollapseAll, onPreviewDrafts, onManageAccess, onToggleActive }) {
  const menu = useActionMenu()
  const disabled = journey?.status === 'disabled'
  return (
    <div className={`wf-node wf-node--root${collapsed ? ' wf-node--collapsed' : ''}`} data-key="root:journey">
      <div className="wf-row">
        <button className="wf-triangle" data-fn="toggleCollapse" data-key="root:journey">
          <span className={`wf-tri-icon ${collapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down'}`}></span>
        </button>
        <button className="wf-bullet wf-bullet--root" data-fn="toggleCollapse" data-key="root:journey"></button>
        <div className="wf-text-wrap">
          <span className="wf-node-text wf-node-text--root">Client Hub</span>
          {disabled && <span className="wf-badge wf-badge--gray" style={{ marginLeft: 8 }}>Disabled</span>}
        </div>
        <ActionMenuButton menu={menu} />
      </div>
      <ActionMenu menu={menu}>
        <ActionMenuItem onClick={onAdd}>Add entry</ActionMenuItem>
        <ActionMenuDivider />
        <ActionMenuItem onClick={onExpandAll}>Expand all</ActionMenuItem>
        <ActionMenuItem onClick={onCollapseAll}>Collapse all</ActionMenuItem>
        <ActionMenuDivider />
        <ActionMenuItem onClick={onPreviewDrafts}>Preview hub as a client…</ActionMenuItem>
        <ActionMenuItem onClick={onManageAccess}>Manage access…</ActionMenuItem>
        <ActionMenuDivider />
        <ActionMenuItem onClick={onToggleActive}>{disabled ? 'Activate hub' : 'Disable hub'}</ActionMenuItem>
      </ActionMenu>
    </div>
  )
}

// A project-phase branch in the author outline — a collapsible section grouping its entries, with
// an "Add entry here" action that adopts the phase silently (presetPhase).
function PhaseBranch({ phase, count, ckey, onAdd, children }) {
  const menu = useActionMenu()
  // Collapsed-by-default + persisted, same as the rest of the outline (survives refresh).
  const collapsed = wf.collapsed[ckey] !== false
  return (
    <div className={`wf-node wf-node--section${collapsed ? ' wf-node--collapsed' : ''}`} data-key={ckey}>
      <div className="wf-row">
        <button className="wf-triangle" data-fn="toggleCollapse" data-key={ckey}>
          <span className={`wf-tri-icon ${collapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down'}`}></span>
        </button>
        <button className="wf-bullet wf-bullet--section" data-fn="toggleCollapse" data-key={ckey}></button>
        <div className="wf-text-wrap">
          <span className="wf-node-text wf-node-text--section">{phase}</span>
          <span className="wf-prog" style={{ color: '#7a9aaa', marginLeft: 8 }}>{count}</span>
        </div>
        <ActionMenuButton menu={menu} />
      </div>
      <ActionMenu menu={menu}>
        <ActionMenuItem onClick={() => { menu.close(); onAdd() }}>Add entry here</ActionMenuItem>
      </ActionMenu>
      {!collapsed && <div className="wf-children">{children}</div>}
    </div>
  )
}

// Users branch (Matt, 2026-10-26; replaced the Subscribers branch) — everyone who has been given a
// Client Hub link, with whether they have signed in (and when last), whether they get email updates,
// and their Getting started tour (docs/CLIENT_HUB_TOUR.md). Each person expands to the five tour
// steps. Email-update subscribers who were never given a personal link are listed too. Lazy-loads on
// first expand (CLAUDE.md lazy-load rule) and reloads on 'journey-updated'.
const TOUR_STEP_LABELS = [
  ['entries', 'How updates work'], ['roadmap', 'Your Roadmap'], ['updates', 'Email updates'],
  ['invoices', 'Your invoices'], ['inspiration', 'Inspiration images'],
]

function UsersBranch({ journeyId }) {
  const key = 'sec:journey:users'
  const collapsed = wf.collapsed[key] !== false
  const [users, setUsers] = useState(null)     // null = not loaded, [] / [...] = loaded
  const hasLoaded = useRef(false)

  function load() {
    if (!journeyId) return
    listJourneyUsers(journeyId).then(({ users }) => setUsers(users || [])).catch(() => setUsers([]))
  }
  function maybeLoad() {
    if (hasLoaded.current || !journeyId) return
    hasLoaded.current = true
    load()
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (!collapsed) maybeLoad() }, [collapsed, journeyId])
  useEffect(() => {
    const onUpd = () => { if (hasLoaded.current) load() }
    window.addEventListener('journey-updated', onUpd)
    return () => window.removeEventListener('journey-updated', onUpd)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [journeyId])

  function toggle() {
    wf.collapsed[key] = !collapsed
    saveViewState()
    window.dispatchEvent(new Event('outline-render'))
  }

  const activeCount = users ? users.filter(u => u.status === 'active' && !u.noLink).length : null

  return (
    <div className={`wf-node wf-node--section${collapsed ? ' wf-node--collapsed' : ''}`} data-key={key}>
      <div className="wf-row">
        <button className="wf-triangle" onClick={toggle}>
          <span className={`wf-tri-icon ${collapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down'}`}></span>
        </button>
        <button className="wf-bullet wf-bullet--section" onClick={toggle}></button>
        <div className="wf-text-wrap">
          <span className="wf-node-text wf-node-text--section">Users</span>
          {activeCount != null && <span className="wf-prog" style={{ color: '#7a9aaa', marginLeft: 8 }}>{activeCount}</span>}
        </div>
      </div>
      {!collapsed && (
        <div className="wf-children">
          {users === null && <div className="wf-node-text" style={{ padding: '6px 0', color: '#7a9aaa' }}>Loading…</div>}
          {users && users.length === 0 && (
            <div className="wf-node-text" style={{ padding: '4px 0', color: '#7a9aaa', fontStyle: 'italic' }}>
              No one has been given access yet. Use the Client Hub ⋯ menu to send access.
            </div>
          )}
          {users && users.map(u => <UserRow key={u.id} user={u} />)}
        </div>
      )}
    </div>
  )
}

function fmtShort(iso) {
  if (!iso) return ''
  const d = new Date(iso)
  return isNaN(d) ? '' : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

function UserRow({ user }) {
  const key = 'jusr:' + user.id
  const revoked = user.status !== 'active'
  const hasTour = !!user.tour
  const collapsed = wf.collapsed[key] !== false
  const t = user.tour
  const tourText = !hasTour ? null
    : t.finishedAt || t.done === t.total ? 'Tour done'
    : t.done ? 'Tour ' + t.done + '/' + t.total
    : 'Tour not started'
  const signIn = user.noLink ? 'No personal link'
    : user.lastOpenedAt ? 'Last in ' + fmtShort(user.lastOpenedAt)
    : 'Never signed in'
  const email = user.subscription === 'active' ? 'Email updates'
    : user.subscription ? 'Unsubscribed' : null

  function toggle() {
    if (!hasTour) return
    wf.collapsed[key] = !collapsed
    saveViewState()
    window.dispatchEvent(new Event('outline-render'))
  }

  return (
    <div className={`wf-node wf-node--item${hasTour && collapsed ? ' wf-node--collapsed' : ''}`} data-key={key}>
      <div className="wf-row">
        {hasTour ? (
          <button className="wf-triangle" onClick={toggle}>
            <span className={`wf-tri-icon ${collapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down'}`}></span>
          </button>
        ) : <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>}
        <span className="wf-bullet wf-bullet--item" style={revoked ? { opacity: 0.4 } : undefined}></span>
        <div className="wf-text-wrap">
          <span className="wf-node-text" style={revoked ? { color: '#9bb0bb', textDecoration: 'line-through' } : undefined}>
            {user.name || user.email}
          </span>
          {user.name && user.email && (
            <span style={{ marginLeft: 8, fontFamily: '"DM Mono",monospace', fontSize: 11, color: '#9bb0bb' }}>{user.email}</span>
          )}
          {user.role === 'owners_rep' && <span className="wf-badge wf-badge--gray" style={{ marginLeft: 8 }} title="Sees the updates, no invoices, proposals or signing">Owner's rep</span>}
          <span style={{ marginLeft: 'auto', display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
            {revoked && <span className="wf-badge wf-badge--gray">Access removed</span>}
            <span className={'wf-badge ' + (user.lastOpenedAt ? 'wf-badge--green' : 'wf-badge--gray')}>{signIn}</span>
            {email && <span className={'wf-badge ' + (user.subscription === 'active' ? 'wf-badge--blue' : 'wf-badge--gray')}>{email}</span>}
            {tourText && <span className={'wf-badge ' + (tourText === 'Tour done' ? 'wf-badge--green' : 'wf-badge--gray')}>{tourText}</span>}
          </span>
        </div>
      </div>
      {hasTour && !collapsed && (
        <div className="wf-children">
          {TOUR_STEP_LABELS.map(([k, label]) => {
            const v = t.steps && t.steps[k]
            const skipped = v && typeof v === 'object' && v.skipped
            const when = typeof v === 'string' ? fmtShort(v) : (v && v.at ? fmtShort(v.at) : '')
            return (
              <div key={k} className="wf-node wf-node--item" data-key={key + ':' + k}>
                <div className="wf-row">
                  <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
                  <span className="wf-bullet wf-bullet--item" style={v ? undefined : { opacity: 0.4 }}></span>
                  <div className="wf-text-wrap">
                    <span className="wf-node-text" style={v ? undefined : { color: '#9bb0bb' }}>{label}</span>
                    <span style={{ marginLeft: 8, fontFamily: '"DM Mono",monospace', fontSize: 11, color: skipped ? '#B7651A' : '#7a9aaa' }}>
                      {skipped ? 'Skipped' + (when ? ' ' + when : '') : v ? (v.derived ? 'Added a picture' : 'Done' + (when ? ' ' + when : '')) : 'Not yet'}
                    </span>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

function EntryNode({ entry, journey, projectId, responded, phaseList, onChanged }) {
  const menu = useActionMenu()
  const [placing, setPlacing] = useState(null)   // 'phase' | 'date' | null
  const mod = getModule(entry.type)
  const key = 'jentry:' + entry.id
  const date = fmtJourneyDate(resolveEntryDate(entry))   // same date the Client Hub shows

  // Sketch ribbon entries expand to show each sketch as its own branch — the outline's only view
  // into entry.data.sketches without opening the full entry editor. Collapsed by default per the
  // outline's collapse convention.
  const sketches = entry.type === 'sketches' && Array.isArray(entry.data?.sketches) ? entry.data.sketches : null
  const isExpandable = !!(sketches && sketches.length)
  const collapsed = wf.collapsed[key] !== false

  function edit() {
    window.dispatchEvent(new CustomEvent('open-journey-entry-modal', { detail: { journeyId: journey.id, entry } }))
  }
  // A milestone's card always sits in the milestone's phase, so moving it means moving the milestone.
  function movePhase() {
    menu.close()
    if (entry.milestone_airtable_id) {
      const ms = (ctx.state?.milestones || []).find(m => m.id === entry.milestone_airtable_id)
      window.dispatchEvent(new CustomEvent('open-move-phase-modal', {
        detail: { msId: entry.milestone_airtable_id, currentPhId: getFirst(ms?.fields?.['Phase']) || null },
      }))
      return
    }
    setPlacing('phase')
  }
  // A milestone card's hand-set date is data.hubDate (empty = the posted date again); every other
  // entry's date is its own data.entryDate.
  function savePlacement({ phase, date }) {
    const d = { ...(entry.data || {}) }
    if (phase !== undefined) d.phase = phase
    if (date !== undefined) {
      if (entry.milestone_airtable_id) { if (date) d.hubDate = date; else delete d.hubDate }
      else { d.entryDate = date; delete d.hubDate }
    }
    return upsertEntry({ id: entry.id, journeyId: journey.id, data: d }).then(onChanged)
  }
  async function togglePublished() {
    const goingLive = !entry.published
    if (goingLive) {
      const ok = await confirmDialog({
        title: 'Publish this entry?',
        message: 'This entry will be visible to the client immediately. Subscribers will receive an email notification.',
        confirmLabel: 'Publish',
      })
      if (!ok) return
    }
    upsertEntry({ id: entry.id, journeyId: journey.id, published: !entry.published }).then(onChanged)
  }
  async function remove() {
    if (!(await confirmDialog({ title: 'Remove entry?', message: 'Remove this entry from the Client Hub?', confirmLabel: 'Remove', danger: true }))) return
    deleteEntry(entry.id).then(onChanged)
  }

  // Persist a change to entry.data.sketches (edit a title, or drop one) — a partial update, so
  // type/title/position/etc. on the entry are left untouched.
  function persistSketches(nextSketches) {
    return upsertEntry({ id: entry.id, journeyId: journey.id, data: { ...(entry.data || {}), sketches: nextSketches } }).then(onChanged)
  }
  function toggle() {
    wf.collapsed[key] = !collapsed
    saveViewState()
    window.dispatchEvent(new Event('outline-render'))
  }

  return (
    <div className={`wf-node wf-node--item${isExpandable && collapsed ? ' wf-node--collapsed' : ''}`} data-key={key}>
      <div className="wf-row">
        {isExpandable ? (
          <button className="wf-triangle" onClick={toggle}>
            <span className={`wf-tri-icon ${collapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down'}`}></span>
          </button>
        ) : (
          <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
        )}
        <span className="wf-bullet wf-bullet--item" onClick={isExpandable ? toggle : undefined}></span>
        <div className="wf-text-wrap">
          <span className="wf-node-text">{resolveEntryTitle(entry, mod)}</span>
          <span className="wf-badge wf-badge--blue" style={{ marginLeft: 8 }}>{mod?.label || entry.type}</span>
          {!entry.published && <span className="wf-badge wf-badge--amber" style={{ marginLeft: 6 }}>Draft</span>}
          {responded && <span className="wf-badge wf-badge--green" style={{ marginLeft: 6 }}>Client responded</span>}
          {sketches && <span className="wf-prog" style={{ color: '#7a9aaa', marginLeft: 8 }}>{sketches.length}</span>}
          {date && <span style={{ marginLeft: 'auto', fontFamily: '"DM Mono",monospace', fontSize: 11, color: '#9bb0bb', flexShrink: 0, paddingLeft: 12 }}>{date}</span>}
        </div>
        <ActionMenuButton menu={menu} />
      </div>
      <ActionMenu menu={menu}>
        <ActionMenuItem onClick={edit}>Edit</ActionMenuItem>
        <ActionMenuItem onClick={movePhase}>{entry.milestone_airtable_id ? 'Move milestone to phase…' : 'Move to phase…'}</ActionMenuItem>
        {entry.type !== 'permitting' && (
          <ActionMenuItem onClick={() => { menu.close(); setPlacing('date') }}>Change hub date…</ActionMenuItem>
        )}
        <ActionMenuDivider />
        <ActionMenuItem onClick={togglePublished}>{entry.published ? 'Unpublish' : 'Publish'}</ActionMenuItem>
        <ActionMenuItem onClick={remove} danger>Remove</ActionMenuItem>
      </ActionMenu>
      {placing && (
        <EntryPlacementModal
          mode={placing}
          title={resolveEntryTitle(entry, mod)}
          phase={resolveEntryPhase(entry)}
          phaseList={phaseList || []}
          date={resolveEntryDate(entry)}
          isMilestone={!!entry.milestone_airtable_id}
          hasHandDate={!!(entry.data && entry.data.hubDate)}
          onSave={savePlacement}
          onClose={() => setPlacing(null)}
        />
      )}
      {isExpandable && !collapsed && (
        <div className="wf-children">
          {sketches.map((s, i) => (
            <SketchItemRow
              key={s.id || i}
              sketch={s}
              entryKey={key}
              index={i}
              projectId={projectId}
              onEditTitle={async () => {
                const next = await promptDialog({ title: 'Sketch title', message: 'Title shown above this sketch on the Client Hub:', defaultValue: s.title || '', placeholder: 'Untitled' })
                if (next === null) return
                persistSketches(sketches.map((x, j) => j === i ? { ...x, title: next.trim() } : x))
              }}
              onRemove={async () => {
                if (!(await confirmDialog({ title: 'Remove sketch?', message: 'Remove this sketch from the ribbon?', confirmLabel: 'Remove', danger: true }))) return
                persistSketches(sketches.filter((_, j) => j !== i))
              }}
            />
          ))}
        </div>
      )}
    </div>
  )
}

// Move an entry to another phase, or change the date it shows on the Client Hub (its place within
// the phase follows the date). A milestone card's date is the posted date unless set by hand here.
function EntryPlacementModal({ mode, title, phase, phaseList, date, isMilestone, hasHandDate, onSave, onClose }) {
  const startDate = (date || '').slice(0, 10)
  const [value, setValue] = useState(mode === 'phase' ? (phaseList.includes(phase) ? phase : '') : startDate)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  const unchanged = mode === 'phase' ? value === phase : value === startDate
  function save(next) {
    if (saving) return
    setSaving(true); setError('')
    onSave(mode === 'phase' ? { phase: next } : { date: next })
      .then(onClose)
      .catch(() => { setSaving(false); setError('Could not save. Please try again.') })
  }
  const lbl = { fontFamily: '"DM Sans",sans-serif', fontSize: 11, fontWeight: 500, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--aj-text-muted)', marginBottom: 8 }
  const btn = { border: 'none', borderRadius: 4, padding: '8px 18px', fontFamily: '"DM Sans",sans-serif', fontSize: 13, fontWeight: 500, cursor: 'pointer' }
  const input = {
    width: '100%', boxSizing: 'border-box', padding: '7px 9px', borderRadius: 4,
    border: '0.5px solid var(--aj-border-mid)', background: 'var(--aj-bg-surface)', color: 'var(--aj-text)',
    fontFamily: mode === 'date' ? '"DM Mono",monospace' : '"DM Sans",sans-serif', fontSize: 13,
  }

  return createPortal(
    <div onClick={e => { if (e.target === e.currentTarget) onClose() }}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 9000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: 'var(--aj-bg-elevated)', borderRadius: 10, border: '0.5px solid var(--aj-border)', padding: '22px 24px', width: 380, maxWidth: '100%', boxShadow: '0 8px 40px rgba(0,0,0,0.18)' }}>
        <div style={{ fontFamily: '"DM Sans",sans-serif', fontWeight: 500, fontSize: 12, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--aj-text)', marginBottom: 16 }}>
          {mode === 'phase' ? 'Move entry' : 'Change hub date'}
        </div>
        <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 13, color: 'var(--aj-text)', padding: '8px 10px', background: 'var(--aj-bg-subtle)', borderRadius: 4, fontWeight: 500, marginBottom: 16 }}>
          {title}
        </div>
        <div style={lbl}>{mode === 'phase' ? 'Phase' : 'Date shown on the Client Hub'}</div>
        {mode === 'phase' ? (
          <select value={value} onChange={e => setValue(e.target.value)} style={input} autoFocus>
            <option value="">Select a phase…</option>
            {phaseList.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
        ) : (
          <input type="date" value={value} onChange={e => setValue(e.target.value)} style={input} autoFocus />
        )}
        <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: 'var(--aj-text-muted)', marginTop: 8, lineHeight: 1.45 }}>
          {mode === 'phase'
            ? 'The client sees it under this phase right away.'
            : isMilestone
              ? 'The card sorts by this date within its phase. Use posted date puts it back.'
              : 'The card sorts by this date within its phase.'}
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center', marginTop: 20 }}>
          {error && <span style={{ fontSize: 12, color: '#c0392b', marginRight: 'auto' }}>{error}</span>}
          {mode === 'date' && isMilestone && hasHandDate && !error && (
            <button onClick={() => save('')} disabled={saving} style={{ ...btn, background: 'transparent', color: 'var(--aj-text-mid)', marginRight: 'auto', padding: '8px 4px' }}>
              Use posted date
            </button>
          )}
          <button onClick={onClose} style={{ ...btn, background: 'transparent', border: '0.5px solid var(--aj-border-mid)', color: 'var(--aj-text-mid)' }}>Cancel</button>
          <button onClick={() => save(value)} disabled={!value || unchanged || saving}
            style={{ ...btn, background: '#07272D', color: 'white', opacity: (!value || unchanged || saving) ? 0.5 : 1 }}>
            {saving ? 'Saving…' : mode === 'phase' ? 'Move' : 'Save date'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}

// A single sketch within an expanded Sketch-ribbon entry. PDF-backed items ("Drawing" badge) open
// straight into ConceptDrawingModal (presenter mode) — the fix path for scale, including sketches
// that were uploaded before the modal reliably opened on upload (see the ConceptDrawingHost mount
// fix) and so never got a scale at all. Plain image items get a lightweight rename instead, since
// there's no separate tool for them to open.
function SketchItemRow({ sketch, entryKey, index, projectId, onEditTitle, onRemove }) {
  const menu = useActionMenu()
  const key = entryKey + ':sk:' + index
  const label = sketch.title || (sketch.isDrawing ? 'Untitled drawing' : 'Untitled sketch')

  function openDrawingTool() {
    window.dispatchEvent(new CustomEvent('open-concept-drawing-modal', {
      detail: { sketchId: sketch.id, projectId, title: sketch.title || 'Sketch', mode: 'presenter' },
    }))
  }

  return (
    <div className="wf-node wf-node--item" data-key={key}>
      <div className="wf-row">
        <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
        <span className="wf-bullet wf-bullet--item"></span>
        <div className="wf-text-wrap">
          <span className="wf-node-text">{label}</span>
          {sketch.type && <span className="wf-badge wf-badge--gray" style={{ marginLeft: 8 }}>{sketch.type}</span>}
          {sketch.isDrawing && <span className="wf-badge wf-badge--blue" style={{ marginLeft: 6 }}>Drawing</span>}
        </div>
        <ActionMenuButton menu={menu} />
      </div>
      <ActionMenu menu={menu}>
        {sketch.isDrawing ? (
          <ActionMenuItem onClick={() => { menu.close(); openDrawingTool() }}>Edit (set scale / measure)</ActionMenuItem>
        ) : (
          <>
            <ActionMenuItem onClick={() => { menu.close(); onEditTitle() }}>Edit title</ActionMenuItem>
            <ActionMenuItem onClick={() => window.open(sketch.url, '_blank', 'noopener')}>Open image</ActionMenuItem>
          </>
        )}
        <ActionMenuDivider />
        <ActionMenuItem onClick={() => { menu.close(); onRemove() }} danger>Remove</ActionMenuItem>
      </ActionMenu>
    </div>
  )
}
